import qs from "qs";
import {
    advancedSearchPathFragment,
    bundleManagerPathFragment,
    canvasPathFragment,
    executionsPathFragment,
    logSubPageKey,
    settingsPathFragment,
    storageAnalysisSubPageKey,
    usersRolesPathFragment,
} from "utils/RouterManager/constants";
import { parseSearchParams } from "utils/RouterManager/searchParams";

export type SubPageKind = "record" | "executions" | "logs" | "canvas";

const WORKSPACE_SUB_PAGE_FLAGS = [
    executionsPathFragment,
    settingsPathFragment,
    storageAnalysisSubPageKey,
    bundleManagerPathFragment,
    logSubPageKey,
    advancedSearchPathFragment,
    usersRolesPathFragment,
] as const;

const getQueryRecordKey = (params: qs.ParsedQs): string | null => {
    const id = params.id;
    const name = params.name;
    if (typeof id === "string" && id.length > 0) {
        return `id:${id}`;
    }
    if (typeof name === "string" && name.length > 0) {
        return `name:${name}`;
    }
    return null;
};

/** Returns the active workspace/container sub-page query flag, or null for the plain record view. */
export const getActiveSubPageFlag = (search: string): string | null => {
    const params = parseSearchParams(search.startsWith("?") ? search.substring(1) : search);
    for (const flag of WORKSPACE_SUB_PAGE_FLAGS) {
        if (flag in params) {
            return flag;
        }
    }
    if ("canvas" in params) {
        return "canvas";
    }
    return null;
};

export const getSubPageKind = (search: string): SubPageKind => {
    const flag = getActiveSubPageFlag(search);
    if (flag === executionsPathFragment) {
        return "executions";
    }
    if (flag === logSubPageKey) {
        return "logs";
    }
    if (flag === "canvas" || flag === canvasPathFragment) {
        return "canvas";
    }
    return "record";
};

/**
 * Whether a sidebar/navigation link targets the same record view as the current URL,
 * including sub-pages (executions, logs, canvas, workspace settings, …).
 */
export const isContainerNavigationLinkActive = (
    link: string,
    currentPathname: string,
    currentSearch: string
): boolean => {
    const origin = window.location.origin;
    try {
        const linkUrl = new URL(link, origin);
        if (linkUrl.pathname !== currentPathname) {
            return false;
        }

        const linkParams = qs.parse(linkUrl.search.substring(1));
        const currentParams = qs.parse(
            currentSearch.startsWith("?") ? currentSearch.substring(1) : currentSearch
        );

        const isWorkspaceRoute = linkUrl.pathname === "/workspace";
        if (
            !isWorkspaceRoute &&
            getQueryRecordKey(linkParams) !== getQueryRecordKey(currentParams)
        ) {
            return false;
        }

        const linkFlag = getActiveSubPageFlag(linkUrl.search);
        const currentFlag = getActiveSubPageFlag(currentSearch);
        if (linkFlag !== currentFlag) {
            return false;
        }

        if (linkFlag === "canvas") {
            return linkParams.canvas === currentParams.canvas;
        }

        return true;
    } catch {
        return false;
    }
};
